import { ErrorBoundary } from '@sentry/react';
import { LibraryCache } from 'base/library_cache';
import { PublicComponent } from 'base/library_service';
import { Button } from 'base/ui/button';
import {
  IconArrowForward,
  IconClear,
  IconCopy,
  IconSearch,
} from 'base/ui/icons';
import { FormInput } from 'base/ui/input';
import { Inline, Rows, SpacerGrow } from 'base/ui/layout';
import { DropdownIcon, MenuButton } from 'base/ui/menu';
import { menuItem } from 'base/ui/menu_model';
import { DividerLine } from 'base/ui/panel';
import { Scrollable } from 'base/ui/scrollable';
import { ToastLayer, ToastQueue } from 'base/ui/toast';
import { TextRegular, TextTiny, TextTinyLight } from 'base/ui/typography';
import { toNested } from 'base/webflow/nested';
import { findReplaceableTextContent } from 'base/webflow/replaceable_content';
import { Component, componentSchema } from 'base/webflow/schema';
import { textIsSimilar } from 'base/webflow/text_similiarity';
import { zodParse } from 'base/zod_parse';
import { observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { NoSlotsComponentView } from './component_renderer';
import {
  baseDecorator,
  composeDecorators,
  makeDropdownDecorator,
  makeFormVisibilityDecorator,
  makeNavbarDecorator,
  makeSliderDecorator,
  makeTabsDecorator,
  NodeDecorator,
} from './decorators';
import styles from './id_mapper_tool.css';
import { makeReadonlyTextOverridesDecorator } from './text_overrides';

export function installComponentIdMapperTool(
  library: LibraryCache,
): React.ComponentType {
  const toastQueue = new ToastQueue();
  return function IdMapperTool() {
    return (
      <>
        <Tool toastQueue={toastQueue} library={library} />
        <ToastLayer queue={toastQueue} />
      </>
    );
  };
}

const Tool = observer(function _Tool({
  toastQueue,
  library,
}: {
  toastQueue: ToastQueue;
  library: LibraryCache;
}) {
  const [comps, setComps] = React.useState<
    | undefined
    | {
        new: Component;
        baseline?: Component;
      }
  >();

  React.useEffect(() => {
    function handlePasteTyped(e: ClipboardEvent) {
      const text =
        e.clipboardData?.getData('application/json') ||
        e.clipboardData?.getData('text/plain');
      if (!text) {
        return toastQueue.push({ label: 'no json data found in clipboard' });
      }
      let value;
      try {
        value = zodParse(componentSchema, JSON.parse(text));
      } catch (e) {
        console.error(e);
        return toastQueue.push({ label: 'error parsing clipboard' });
      }
      setComps({ new: value });
    }
    // typescript doesn't know about clipboard events
    const handlePaste = handlePasteTyped as (e: Event) => void;
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [toastQueue]);

  const model = React.useMemo(
    () => comps?.baseline && new MappingModel(comps.baseline, comps.new),
    [comps],
  );

  const visibility = React.useMemo(
    () =>
      observable({
        openMenus: true,
        showFormFields: true,
        showFormError: true,
        showFormSuccess: true,
        sliderIndex: 0,
        tabIndex: 0,
      }),
    [],
  );
  const sharedDecorator = React.useMemo<NodeDecorator>(
    () =>
      composeDecorators(
        baseDecorator,
        makeDropdownDecorator(visibility),
        makeNavbarDecorator(visibility),
        makeFormVisibilityDecorator(visibility),
        makeSliderDecorator(visibility),
        makeTabsDecorator(visibility),
      ),
    [visibility],
  );

  return (
    <div className={styles.tool}>
      <div className={styles.header}>
        {model ? (
          <ToolFixedHeader model={model} sharedDecorator={sharedDecorator} />
        ) : comps ? (
          <PickLeftComponentHeader
            rightComp={comps.new}
            sharedDecorator={sharedDecorator}
          />
        ) : (
          <TextRegular>
            Paste a component from Webflow master file to start mapping. NOTE:
            it needs to be copied via "Collect JSONs" then "Copy content".
          </TextRegular>
        )}
      </div>
      <Scrollable>
        {model ? (
          <ToolEditArea model={model} />
        ) : comps ? (
          <FindBaselineComponent
            for={comps.new}
            onFound={(baseline) => setComps({ ...comps, baseline })}
            library={library}
            toastQueue={toastQueue}
          />
        ) : undefined}
      </Scrollable>
      {model && <Footer model={model} toastQueue={toastQueue} />}
    </div>
  );
});

const Footer = observer(function _Footer({
  model,
  toastQueue,
}: {
  model: MappingModel;
  toastQueue: ToastQueue;
}) {
  return (
    <div className={styles.footer}>
      <Inline spacing={16} yalign="center">
        <Rows spacing={0}>
          <TextRegular>{model.rightIdChanges.size} ID changes made</TextRegular>
          <TextTinyLight>Scroll to see all fields</TextTinyLight>
          <TextTinyLight>Paste another component to start again</TextTinyLight>
        </Rows>
        <SpacerGrow />
        <Button
          icon={IconCopy}
          label="Copy"
          labelNoWrap
          tooltip={
            model.rightIdChanges.size
              ? 'Copy ID Mappings'
              : 'You have not created any mappings yet'
          }
          disabled={model.rightIdChanges.size === 0}
          size="large"
          onClick={() => {
            navigator.clipboard.writeText(
              JSON.stringify(
                Object.fromEntries(model.rightIdChanges.entries()),
                undefined,
                2,
              ),
            );
            toastQueue.push({ label: 'Copied' });
          }}
        />
      </Inline>
    </div>
  );
});

const COMP_VIEW_PROPS = {
  innerWidth: 1440,
  innerViewHeight: 800,
  outerWidth: 600,
};

function PickLeftComponentHeader({
  rightComp,
  sharedDecorator,
}: {
  rightComp: Component;
  sharedDecorator: NodeDecorator;
}) {
  return (
    <div className={styles.mappingGrid}>
      <div />
      <IconArrowForward />
      <ErrorBoundary
        fallback={<TextRegular>Error rendering component</TextRegular>}
      >
        <NoSlotsComponentView
          {...COMP_VIEW_PROPS}
          component={rightComp}
          decorator={sharedDecorator}
        />
      </ErrorBoundary>
    </div>
  );
}

function FindBaselineComponent({
  for: rightComp,
  onFound,
  library,
  toastQueue,
}: {
  for: Component;
  onFound: (baseline: Component) => void;
  library: LibraryCache;
  toastQueue: ToastQueue;
}) {
  const [search, setSearch] = React.useState('');
  const [results, setResults] = React.useState<PublicComponent[]>([]);

  React.useEffect(() => {
    setSearch(guessSearchForComponent(rightComp));
  }, [rightComp]);

  React.useEffect(() => {
    if (!search) {
      setResults([]);
      return;
    }
    const results = library.findPublicComponents({ search });
    let cancelled = false;
    results
      .then((res) => {
        if (cancelled) return;
        setResults(res.components);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error(e);
        toastQueue.push({ label: 'Error searching library' });
      });
    return () => {
      cancelled = true;
    };
  }, [search, library, toastQueue]);

  const useComponent = React.useCallback(
    async (comp: PublicComponent) => {
      const remove = toastQueue.addPersistent(() => ({
        label: `Loading ${comp.name}...`,
      }));
      try {
        onFound(
          await library.getPublicComponentJson(comp.componentSlug, {
            bypassCloudfront: true,
          }),
        );
      } catch (e) {
        console.error(e);
        toastQueue.push({ label: 'Error loading component' });
      } finally {
        remove();
      }
    },
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toastQueue],
  );

  return (
    <Rows spacing={16} xalign="start">
      <TextRegular>Find the component in the library</TextRegular>
      <FormInput
        size="large"
        iconStart={IconSearch}
        value={search}
        onChange={setSearch}
        type="search"
        placeholder="Search Query"
      />
      <DividerLine />
      {results.map((comp) => (
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react-hooks/rules-of-hooks
        <Button key={comp.componentSlug} onClick={() => useComponent(comp)}>
          <Inline spacing={16}>
            <img
              src={comp.thumbnailUrl}
              className={styles.componentImage}
              style={{
                aspectRatio: comp.thumbnailWidth / comp.thumbnailHeight,
              }}
            />
            <Rows spacing={8} xalign="start">
              <TextRegular>{comp.name}</TextRegular>
              <TextTiny>
                {comp.categorySlug}/{comp.componentSlug}
              </TextTiny>
              <TextTiny>{comp.kind}</TextTiny>
            </Rows>
            <div />
          </Inline>
        </Button>
      ))}
      {results.length === 0 && search && (
        <TextRegular>No results found - try changing the search</TextRegular>
      )}
    </Rows>
  );
}

function guessSearchForComponent(comp: Component): string {
  const nodes = toNested(comp.payload.nodes);
  const root = nodes[0];
  if (!root || root.text || root.classes.length === 0) return '';
  const rootStyle = comp.payload.styles.find((s) => s._id === root.classes[0]);
  if (!rootStyle) return '';
  return rootStyle.name.replaceAll('_component', '');
}

const ToolFixedHeader = observer(function _ToolFixedHeader({
  model,
  sharedDecorator,
}: {
  model: MappingModel;
  sharedDecorator: NodeDecorator;
}) {
  const leftCompDecorator = React.useMemo(
    () =>
      composeDecorators(
        sharedDecorator,
        makeReadonlyTextOverridesDecorator(model.getLeftText, () => false),
      ),
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sharedDecorator],
  );
  const rightCompDecorator = React.useMemo(
    () =>
      composeDecorators(
        sharedDecorator,
        makeReadonlyTextOverridesDecorator(model.getRightText, () => false),
      ),
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sharedDecorator],
  );

  return (
    <div className={styles.mappingGrid}>
      <ErrorBoundary>
        <NoSlotsComponentView
          {...COMP_VIEW_PROPS}
          component={model.leftComp}
          decorator={leftCompDecorator}
        />
      </ErrorBoundary>
      <IconArrowForward />
      <ErrorBoundary>
        <NoSlotsComponentView
          {...COMP_VIEW_PROPS}
          component={model.rightComp}
          decorator={rightCompDecorator}
        />
      </ErrorBoundary>
    </div>
  );
});

const ToolEditArea = observer(function _ToolInner({
  model,
}: {
  model: MappingModel;
}) {
  return (
    <Rows spacing={32}>
      <div className={styles.mappingGrid}>
        {model.leftTextFields.map(({ name, text, leftId }) => (
          <React.Fragment key={'l' + leftId}>
            <Rows spacing={0}>
              <TextRegular>{name}</TextRegular>
              <TextTinyLight lineClamp={1}>{text}</TextTinyLight>
            </Rows>
            <IconArrowForward />
            <EditMappingFrom leftId={leftId} model={model} />
          </React.Fragment>
        ))}
        {model.unmappedInRight.map(({ name, text, rightId }) => (
          <React.Fragment key={'r' + rightId}>
            <TextTinyLight>(none)</TextTinyLight>
            <IconArrowForward />
            <Rows spacing={0}>
              <TextRegular color="purple">{name} (new)</TextRegular>
              <TextTinyLight color="purple" lineClamp={1}>
                {text}
              </TextTinyLight>
            </Rows>
          </React.Fragment>
        ))}
      </div>
    </Rows>
  );
});

const EditMappingFrom = observer(function _EditMappingFrom({
  leftId,
  model,
}: {
  leftId: string;
  model: MappingModel;
}) {
  // Case 1 - There's an match in the right component as is.
  if (model.identicalIds.has(leftId)) {
    const match1 = model.rightContent.findIndex((c) => c.id === leftId);
    if (match1 != -1) {
      const { original } = model.rightContent[match1];
      return (
        <Rows spacing={0}>
          <TextRegular>
            <Inline spacing={8}>
              [{indexToLetter(match1)}]
              <Button
                size="small"
                label="Clear"
                icon={IconClear}
                onClick={() => model.identicalIds.delete(leftId)}
              />
            </Inline>
          </TextRegular>
          <TextTinyLight lineClamp={1}>{original}</TextTinyLight>
        </Rows>
      );
    }
  }

  // Case 2 - There's no match in the right component, so the user needs
  //   to make a mapping.
  const match2 = model.rightContent.findIndex(
    (c) => model.rightIdChanges.get(c.id) === leftId,
  );
  if (match2 != -1) {
    const { original, id: rightId } = model.rightContent[match2];
    return (
      <Rows spacing={0}>
        <TextRegular>
          <Inline spacing={8}>
            [{indexToLetter(match2)}]
            <Button
              size="small"
              label="Clear"
              icon={IconClear}
              onClick={() => model.rightIdChanges.delete(rightId)}
            />
          </Inline>
        </TextRegular>
        <TextTinyLight lineClamp={1}>{original}</TextTinyLight>
      </Rows>
    );
  }

  return (
    <MenuButton
      label="Map to..."
      variant="primary"
      background="negative"
      iconRight={DropdownIcon}
      menu={model.unmappedInRight.map((c) =>
        menuItem({
          label: `${c.name} ${c.text}`,
          onClick: () => model.rightIdChanges.set(c.rightId, leftId),
        }),
      )}
    />
  );
});

class MappingModel {
  /* The proposed changes to the right component.  Right existing id -> right replaced id */
  readonly rightIdChanges = observable.map<string, string>();

  /* IDs that are the same in both left and right components */
  readonly identicalIds = observable.set<string>();

  constructor(readonly leftComp: Component, readonly rightComp: Component) {
    // First pass: find nodes that have the same ID in both left and right, which don't need mapping.
    // Do this first to avoid them from being mapped to other nodes in the second pass.
    for (const right of this.rightContent) {
      if (this.leftContent.some((n) => n.id === right.id)) {
        this.identicalIds.add(right.id);
      }
    }
    // Second pass: auto-map similar text fields.
    for (const right of this.rightContent) {
      if (this.identicalIds.has(right.id)) continue;
      const leftUsedIds = [...this.rightIdChanges.values()];
      for (const left of this.leftContent) {
        // this left has already been used
        if (this.identicalIds.has(left.id) || leftUsedIds.includes(left.id))
          continue;
        if (!textIsSimilar(left.original, right.original)) continue;
        this.rightIdChanges.set(right.id, left.id);
        break;
      }
    }
  }

  private readonly leftNodes = toNested(this.leftComp.payload.nodes);
  private readonly rightNodes = toNested(this.rightComp.payload.nodes);
  private readonly leftContent = findReplaceableTextContent(this.leftNodes);
  readonly rightContent = findReplaceableTextContent(this.rightNodes);

  get leftTextFields() {
    return this.leftContent.map(({ id: leftId, original }, idx) => {
      return {
        leftId: leftId,
        name: `[${idx + 1}]`,
        text: original,
      };
    });
  }

  get mappingTargetOptions() {
    return this.rightContent.flatMap(
      ({ id: rightId, original: rightText }, idx) => {
        if (this.leftContent.some((c) => c.id === rightId)) return [];
        return {
          label: `[${indexToLetter(idx)}] ${rightText}`,
          value: rightId,
        };
      },
    );
  }

  get unmappedInRight() {
    const leftIds = new Set(this.leftContent.map((c) => c.id));
    return this.rightContent.flatMap((c, idx) => {
      // Case 1 - There's an match in the right component as is.
      //   It is not an added text field.
      if (leftIds.has(c.id) && this.identicalIds.has(c.id)) return [];

      // Case 2 - There's no match in the right component, so the user needs
      //  to make a mapping.
      //  We need to check "rightIdChanges" to see if this is a mapping has
      //  been made.
      const newRightId = this.rightIdChanges.get(c.id);
      if (newRightId && leftIds.has(newRightId)) return [];

      return {
        rightId: c.id,
        name: `[${indexToLetter(idx)}]`,
        text: c.original,
      };
    });
  }

  readonly getLeftText = (nodeId: string) => {
    const idx = this.leftContent.findIndex((c) => c.id === nodeId);
    if (idx === -1) return;
    const { original } = this.leftContent[idx];
    return { type: 'override', text: `[${idx + 1}] ${original}` } as const;
  };

  readonly getRightText = (nodeId: string) => {
    const idx = this.rightContent.findIndex((c) => c.id === nodeId);
    if (idx === -1) return;
    const { original } = this.rightContent[idx];
    return {
      type: 'override',
      text: `[${indexToLetter(idx)}] ${original}`,
    } as const;
  };
}

function indexToLetter(index: number): string {
  if (index >= 26) {
    return (
      indexToLetter(Math.floor(index / 26) - 1) + indexToLetter(index % 26)
    );
  }
  return String.fromCharCode('A'.charCodeAt(0) + index);
}
